import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { schedulePull } from './offline';

/**
 * WebSocket mit automatischem Wiederverbinden (WLAN-Aussetzer, Server-Neustart).
 * Die Nachrichten melden nur „etwas hat sich geändert“; Daten kommen weiter per HTTP.
 */
export function connectEvents(path: string, onMessage: (data: { kind: string; id?: string | null }) => void): () => void {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry = 0;
  let timer: number | undefined;
  const open = () => {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    ws = new WebSocket(`${proto}://${location.host}${path}`);
    ws.onopen = () => {
      // Nach einer Unterbrechung alles einmal neu laden
      if (retry > 0) onMessage({ kind: 'reconnect' });
      retry = 0;
    };
    ws.onmessage = (e) => {
      try {
        onMessage(JSON.parse(String(e.data)));
      } catch {
        /* unbekannte Nachricht */
      }
    };
    ws.onclose = (e) => {
      if (closed || e.code === 4401 || e.code === 4404) return;
      retry++;
      timer = window.setTimeout(open, Math.min(15_000, 500 * 2 ** retry));
    };
  };
  open();
  return () => {
    closed = true;
    window.clearTimeout(timer);
    ws?.close();
  };
}

/** DM-Ansicht: Karten, Kämpfe und Spieleransicht aktuell halten (z. B. zweites Fenster am TV). */
export function useCampaignEvents(cid: string, player = false) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!cid) return;
    const pending = new Set<string>();
    let timer: number | undefined;
    const flush = () => {
      for (const key of pending) {
        const [kind, id] = key.split(':');
        if (kind === 'maps') void qc.invalidateQueries({ queryKey: id ? ['map', cid, id] : ['map', cid] });
        else if (kind === 'combats') {
          void qc.invalidateQueries({ queryKey: ['combats', cid] });
          if (id) void qc.invalidateQueries({ queryKey: ['combat', cid, id] });
          // Tokens zeigen den Kampfstatus
          void qc.invalidateQueries({ queryKey: ['map', cid] });
        } else if (kind === 'screen') void qc.invalidateQueries({ queryKey: ['screen', cid] });
        else if (kind === 'catalog') void qc.invalidateQueries({ queryKey: ['catalog', cid] });
        else if (kind === 'polls') {
          for (const k of ['polls', 'poll', 'ballot', 'schedule']) void qc.invalidateQueries({ queryKey: [k, cid] });
        }
        else if (kind === 'reconnect') {
          for (const k of ['map', 'combat', 'combats', 'screen', 'polls', 'poll', 'ballot', 'schedule']) void qc.invalidateQueries({ queryKey: [k, cid] });
        }
      }
      pending.clear();
    };
    return connectEvents(`/api/ws/campaign/${cid}`, (e) => {
      // Lokale Kopie nachziehen (auch Notizen – offene Editoren behalten ihren Entwurf)
      schedulePull(cid);
      // Notizen nicht automatisch neu laden – das würde offene Editoren stören (Spieler haben keine)
      if (e.kind === 'notes') {
        if (player) {
          for (const k of ['notes', 'note', 'discoveries', 'graph', 'hierarchy', 'notesOfType']) void qc.invalidateQueries({ queryKey: [k, cid] });
        }
        return;
      }
      pending.add(`${e.kind}:${e.id ?? ''}`);
      window.clearTimeout(timer);
      timer = window.setTimeout(flush, 120);
    });
  }, [cid, qc, player]);
}
